import type { RemoveIndexSignature } from 'src/types/removeIndexSignature';

import { computed, watch } from 'vue';
import { ComposerTranslation, LocaleMessageValue, VueMessageType } from 'vue-i18n';
import { RouteLocationNormalizedLoaded, Router } from 'vue-router';

import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Filesystem } from '@capacitor/filesystem';
import { Action, ActionPerformed, LocalNotifications } from '@capacitor/local-notifications';
import { Intent, SendIntent } from '@mindlib-capacitor/send-intent';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { IntentDataDetails } from 'boot/send-intent';
import { normalizeAlarmAggregateId, useAlarmStore } from 'src/stores/alarm';
import { AlarmActionOutcome, outcomeFromAlarmActionResult } from 'src/stores/alarm/alarmActionOutcome';
import { DelayTheAlarmCommand, SnoozeOption } from 'src/stores/alarm/commands/DelayTheAlarmCommand';
import { useAuthenticationStore } from 'src/stores/authentication';
import { useDocumentUploadStore } from 'src/stores/documentUpload';
import { useUserStore } from 'src/stores/user';
import { useUserStateStore } from 'src/stores/userState';
import { RuntimeStoreContext } from 'src/types/runtime/piniaRuntimeContext';

import { openDetails } from 'src/components/workspace/menu/functions';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';
import { AutomaticUserState } from 'src/models/userState';

import { serializeActivityListDateRouteParam } from 'src/router/activityListDateRouteParam';

import { AppService } from 'src/services/app/app.service';

import { CommandBus } from 'src/util/command/CommandBus';
import { translatedModuleName, urlForInstance } from 'src/util/common';
import { EventBus } from 'src/util/eventBus/EventBus';
import { getBrowserName, getPlatformName } from 'src/util/platform';
import { isRunningInNativeCapacitorApp } from 'src/util/platform/browserInteractionCapabilities';
import { QueryStore } from 'src/util/query/QueryStore';

type AlarmNotificationId = number | string;

type AlarmNotificationExtra = {
	id: AlarmNotificationId;
	type: string;
};

type NativeAlarmType = 'appointment' | 'task' | 'taskOwner' | 'taskOwnerAlways';

type AlarmChangedPublisher = {
	emit: (event: 'alarm-changed', payload: { id: string; type: string }) => void;
};

const NATIVE_ALARM_ACTIONS: SnoozeOption[] = [
	'snooze_ok',
	'snooze_5_min',
	'snooze_15_min',
	'snooze_30_min',
	'snooze_1_hour',
	'snooze_2_hour',
	'snooze_4_hour',
	'snooze_1_day',
];

const NATIVE_ALARM_TYPES: NativeAlarmType[] = ['appointment', 'task', 'taskOwner', 'taskOwnerAlways'];

const isAlarmNotificationExtra = (value: unknown): value is AlarmNotificationExtra => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}

	const candidate = value as Record<string, unknown>;

	return (typeof candidate.id === 'number' || typeof candidate.id === 'string') && typeof candidate.type === 'string';
};

const isNativeAlarmAction = (actionId: string): actionId is SnoozeOption => {
	return NATIVE_ALARM_ACTIONS.includes(actionId as SnoozeOption);
};

const isNativeAlarmType = (type: string): type is NativeAlarmType => {
	return NATIVE_ALARM_TYPES.includes(type as NativeAlarmType);
};

export class CapacitorAppService extends AppService {
	protected splitViewEnabled = false;

	constructor(
		t: ComposerTranslation<
			{
				[p: string]: LocaleMessageValue<VueMessageType>;
			},
			string,
			RemoveIndexSignature<{
				[p: string]: LocaleMessageValue<VueMessageType>;
			}>,
			never,
			string,
			string
		>,
		runtimeStore: RuntimeStoreContext,
		router: Router,
		route: RouteLocationNormalizedLoaded,
		queryStore: QueryStore,
		commandBus: CommandBus,
		eventBus: EventBus,
		window: Window,
		splitViewEnabled: boolean
	) {
		super(t, runtimeStore, router, route, queryStore, commandBus, eventBus, window, splitViewEnabled);
	}

	capacitorAppHasMovedToTheForeground() {
		if (!isRunningInNativeCapacitorApp()) {
			return;
		}

		const userStateStore = useUserStateStore();
		const subscribedAutomaticUserStates = userStateStore.subscribedAutomaticUserStates;

		if (subscribedAutomaticUserStates.length > 0) {
			void BackgroundGeolocation.startGeofences();
		} else {
			void userStateStore.deleteActiveAutomaticUserState({
				activeAutomaticUserState: undefined,
				commandBus: this.commandBus,
			});
		}
	}

	async initializeGeofencing() {
		if (!isRunningInNativeCapacitorApp()) {
			return;
		}

		const userStateStore = useUserStateStore();
		const authenticationStore = useAuthenticationStore();
		const activeInstanceAuthenticationConfiguration = computed(
			(): InstanceAuthenticationConfiguration | null =>
				authenticationStore.activeInstanceAuthenticationConfiguration
		);
		if (activeInstanceAuthenticationConfiguration.value === null) {
			return;
		}

		const url = await urlForInstance(activeInstanceAuthenticationConfiguration.value.name.toLowerCase());

		const subscribedAutomaticUserStates = userStateStore.subscribedAutomaticUserStates;

		if (subscribedAutomaticUserStates.length > 0) {
			BackgroundGeolocation.onGeofence((event) => {
				if (
					Object.prototype.hasOwnProperty.call(event, 'extras') &&
					Object.prototype.hasOwnProperty.call(event.extras, 'locationId')
				) {
					const automaticUserState = userStateStore.subscribedAutomaticUserStates.find(
						(automaticUserState) => automaticUserState.id === parseInt(event.extras?.locationId as string)
					);

					if (automaticUserState) {
						if (event.action === 'ENTER' || event.action === 'DWELL') {
							void userStateStore.setActiveAutomaticUserState({
								activeAutomaticUserState: automaticUserState,
								commandBus: this.commandBus,
							});
						} else if (event.action === 'EXIT') {
							void userStateStore.deleteActiveAutomaticUserState({
								activeAutomaticUserState: automaticUserState,
								commandBus: this.commandBus,
							});
						}
					}
				}
			});

			let backgroundPermissionRationaleMessage = '';
			let backgroundPermissionRationalePositiveAction = '';
			let backgroundPermissionRationaleTitle = '';

			if (navigator.language === 'de-DE') {
				backgroundPermissionRationaleMessage =
					'Damit {applicationName} das Geofencing auch durchführen kann, wenn die App im Hintergrund oder geschlossen ist, erteilen Sie bitte die Berechtigung "{backgroundPermissionOptionLabel}"';
				backgroundPermissionRationalePositiveAction = 'Ändern in "{backgroundPermissionOptionLabel}"';
				backgroundPermissionRationaleTitle =
					'Erlauben Sie {applicationName} den Standort des Geräts im abzufragen auch wenn die App im Hintergrund oder geschlossen ist?';
			} else {
				backgroundPermissionRationaleMessage =
					'To enable {applicationName} to perform geofencing even when the app is closed or not in use, please grant the "{backgroundPermissionOptionLabel}" permission';
				backgroundPermissionRationalePositiveAction = 'Change to "{backgroundPermissionOptionLabel}"';
				backgroundPermissionRationaleTitle =
					'Do you allow {applicationName} to query the location of the device even when the app is closed or not in use?';
			}

			await BackgroundGeolocation.ready({
				allowIdenticalLocations: true,
				autoSync: true,
				backgroundPermissionRationale: {
					message: backgroundPermissionRationaleMessage,
					negativeAction: this.t('backgroundPermissionRationale_negativeAction'),
					positiveAction: backgroundPermissionRationalePositiveAction,
					title: backgroundPermissionRationaleTitle,
				},
				debug: false,
				desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
				disableElasticity: false,
				enableHeadless: false,
				foregroundService: true,
				geofenceModeHighAccuracy: true,
				locationAuthorizationAlert: {
					cancelButton: this.t('locationAuthorizationAlert_cancelButton'),
					instructions: this.t('locationAuthorizationAlert_instructions'),
					settingsButton: this.t('locationAuthorizationAlert_settingsButton'),
					titleWhenNotEnabled: this.t('locationAuthorizationAlert_titleWhenNotEnabled'),
					titleWhenOff: this.t('locationAuthorizationAlert_titleWhenOff'),
				},
				locationAuthorizationRequest: 'Always',
				logLevel: process.env.DEV
					? BackgroundGeolocation.LOG_LEVEL_VERBOSE
					: BackgroundGeolocation.LOG_LEVEL_ERROR,
				notification: {
					text: this.t('notification_text'),
					title: this.t('notification_title'),
				},
				persistMode: BackgroundGeolocation.PERSIST_MODE_GEOFENCE,
				showsBackgroundLocationIndicator: true,
				startOnBoot: true,
				stopOnTerminate: false,
				url: `${url}/userstatus/userstatus/geofence`,
				useSignificantChangesOnly: false,
			}).then(() => {
				this.capacitorAppHasMovedToTheForeground();
			});
		} else {
			void userStateStore.deleteActiveAutomaticUserState({
				activeAutomaticUserState: undefined,
				commandBus: this.commandBus,
			});
		}
	}

	initializeTheApp(): void {
		super.initializeTheApp();

		if (!isRunningInNativeCapacitorApp()) {
			return;
		}

		this.registerPushNotificationActions();
		this.registerSendIntent();
		void this.initializeGeofencing().then(() => {
			this.watchSubscribedAutomaticUserStates();
			this.watchAuthenticatedUserId();
		});
	}

	registerPushNotificationActions() {
		if (isRunningInNativeCapacitorApp()) {
			const alarmStore = useAlarmStore();
			let alarmNotificationActions: Array<Action> = [];
			if (Capacitor.getPlatform() === 'android') {
				alarmNotificationActions = [
					{
						foreground: false,
						id: 'snooze_ok',
						title: this.t('alarm_snooze_ok'),
					},
					{
						foreground: false,
						id: 'snooze_15_min',
						title: this.t('alarm_snooze_15_min'),
					},
					{
						foreground: false,
						id: 'snooze_1_hour',
						title: this.t('alarm_snooze_1_day'),
					},
				];
			} else {
				alarmNotificationActions = [
					{
						foreground: false,
						id: 'snooze_ok',
						title: this.t('alarm_snooze_ok'),
					},
					{
						foreground: false,
						id: 'snooze_5_min',
						title: this.t('alarm_snooze_5_min'),
					},
					{
						foreground: false,
						id: 'snooze_15_min',
						title: this.t('alarm_snooze_15_min'),
					},
					{
						foreground: false,
						id: 'snooze_30_min',
						title: this.t('alarm_snooze_30_min'),
					},
					{
						foreground: false,
						id: 'snooze_1_hour',
						title: this.t('alarm_snooze_1_hour'),
					},
					{
						foreground: false,
						id: 'snooze_2_hour',
						title: this.t('alarm_snooze_2_hours'),
					},
					{
						foreground: false,
						id: 'snooze_4_hour',
						title: this.t('alarm_snooze_4_hours'),
					},
					{
						foreground: false,
						id: 'snooze_1_day',
						title: this.t('alarm_snooze_1_day'),
					},
				];
			}

			void LocalNotifications.registerActionTypes({
				types: [
					{
						actions: alarmNotificationActions,
						id: 'snooze',
					},
				],
			})
				.then(async () => {
					void (await LocalNotifications.addListener(
						'localNotificationActionPerformed',
						(notificationAction: ActionPerformed) => {
							const notificationExtra: unknown = notificationAction.notification.extra;

							if (!isAlarmNotificationExtra(notificationExtra)) {
								return;
							}

							if (notificationAction.actionId === ('tap' as string)) {
								void this.handleNativeAlarmTap(notificationExtra).catch(() => 'failure');
							} else {
								void this.handleNativeAlarmSnooze(notificationExtra, notificationAction.actionId).catch(
									() => 'failure'
								);
							}
						}
					));
				})
				.then(() => {
					this.alarmFetchInterval = setInterval(() => {
						void alarmStore.fetchAlarms({
							localNotifications: LocalNotifications,
							queryStore: this.queryStore,
							t: this.t,
						});
					}, 900000);
				});
		}
	}

	private async handleNativeAlarmTap(notificationExtra: AlarmNotificationExtra): Promise<AlarmActionOutcome> {
		const aggregateId = normalizeAlarmAggregateId(notificationExtra.id);
		const authenticatedUserInformation = useUserStore().authenticatedUserInformation;
		const moduleName =
			notificationExtra.type === 'appointment'
				? 'module_appointment'
				: isNativeAlarmType(notificationExtra.type)
					? 'module_task'
					: undefined;
		if (aggregateId === undefined || moduleName === undefined) {
			return 'noop';
		}

		if (authenticatedUserInformation == null) {
			return 'failure';
		}

		const outcome = await this.handleNativeAlarmSnooze(notificationExtra, 'snooze_ok');
		if (outcome !== 'success') {
			return outcome;
		}

		const serializedAggregateId = JSON.stringify({
			entryId: aggregateId,
			moduleName,
			sourceContainerId: 0,
			targetContainerId: 0,
			userId: authenticatedUserInformation.id,
		});
		openDetails(
			this.router,
			this.route,
			this.runtimeStore,
			this.eventBus,
			false,
			translatedModuleName(this.t, moduleName),
			serializedAggregateId,
			moduleName,
			'module'
		);
		return 'success';
	}

	private async handleNativeAlarmSnooze(
		notificationExtra: AlarmNotificationExtra,
		actionId: string
	): Promise<AlarmActionOutcome> {
		const aggregateId = normalizeAlarmAggregateId(notificationExtra.id);
		if (aggregateId === undefined || !isNativeAlarmAction(actionId) || !isNativeAlarmType(notificationExtra.type)) {
			return 'noop';
		}
		if (this.commandBus === undefined) {
			return 'failure';
		}

		try {
			const commandResponse = await this.commandBus.sendCommand<{ success?: boolean }>(
				new DelayTheAlarmCommand(notificationExtra.type, aggregateId, actionId)
			);
			if (commandResponse === null || commandResponse?.data?.success === false) {
				return 'failure';
			}
			const acquisitionResult = await useAlarmStore().backgroundFetchAlarms({
				browser: getBrowserName(),
				localNotifications: LocalNotifications,
				platform: getPlatformName(),
				queryStore: this.queryStore,
				t: this.t,
			});
			const acquisitionOutcome = outcomeFromAlarmActionResult(acquisitionResult);
			if (acquisitionOutcome !== 'success') {
				return acquisitionOutcome;
			}

			const publisher = this.alarmChangedPublisher();
			if (publisher === undefined) {
				return 'failure';
			}

			publisher.emit('alarm-changed', { id: String(aggregateId), type: notificationExtra.type });
			return 'success';
		} catch {
			return 'failure';
		}
	}

	private alarmChangedPublisher(): AlarmChangedPublisher | undefined {
		const eventBus = this.eventBus as unknown as Partial<AlarmChangedPublisher>;
		return typeof eventBus.emit === 'function' ? (eventBus as AlarmChangedPublisher) : undefined;
	}

	registerSendIntent() {
		this.window.addEventListener('sendIntentReceived', () => {
			void SendIntent.checkSendIntentReceived()
				.then((result: Intent) => {
					const intendDataToStore: Array<IntentDataDetails> = [];
					if (result) {
						intendDataToStore.push({
							description: result.description,
							title: decodeURI(result.title ? result.title.replace(/^.*[\\/]/, '') : ''),
							type: result.type,
							url: result.url,
						});

						if (Object.prototype.hasOwnProperty.call(result, 'additionalItems')) {
							(result.additionalItems as Array<IntentDataDetails>).forEach((item) => {
								intendDataToStore.push({
									description: item.description,
									title: decodeURI(item.title ? item.title.replace(/^.*[\\/]/, '') : ''),
									type: item.type,
									url: item.url,
								});
							});
						}
						const activeInstanceAuthenticationConfiguration =
							useAuthenticationStore().activeInstanceAuthenticationConfiguration;
						if (activeInstanceAuthenticationConfiguration !== null) {
							intendDataToStore.forEach((intendData) => {
								Filesystem.readFile({
									path: decodeURIComponent(<string>intendData.url),
								})
									.then((content) => {
										const filename = intendData.title ? intendData.title : '';

										useDocumentUploadStore().enqueue({
											authenticatedUserId: useUserStore().authenticatedUserInformation
												?.id as number,
											commandBus: this.commandBus,
											documentBoxId: 0,
											idoLinkings: [],
											sources: [
												{
													data: `data:${intendData.type as string};base64,${
														content.data as string
													}`,
													kind: 'base64',
													name: filename,
													type: intendData.type as string,
												},
											],
										});

										const targetDate = serializeActivityListDateRouteParam(
											new Date().toISOString().split('T')[0]
										);

										void Device.getInfo().then((deviceInfo) => {
											if (deviceInfo.platform === 'android') {
												SendIntent.finish();
											}
										});

										if (
											this.router.currentRoute.value.name === 'documentList' ||
											JSON.stringify(this.router.currentRoute.value.params) ===
												JSON.stringify({
													date: targetDate,
													source: 'main',
													view: 'open',
												})
										) {
											return;
										}

										void this.router.push({
											name: 'documentList',
											params: {
												date: targetDate,
												source: 'main',
												view: 'open',
											},
										});
									})
									.catch((err) => console.error(err));
							});
						}
					}
				})
				.catch((err) => console.error(err));
		});
	}

	watchAuthenticatedUserId() {
		if (!isRunningInNativeCapacitorApp()) {
			return;
		}

		const userStore = useUserStore();
		const userStateStore = useUserStateStore();
		watch(
			() => userStore.authenticatedUserInformation?.id as number,

			async (authenticatedUserId: null | number) => {
				if (authenticatedUserId !== null && authenticatedUserId !== undefined && authenticatedUserId > 0) {
					const subscribedAutomaticUserStates = userStateStore.subscribedAutomaticUserStates;

					if (subscribedAutomaticUserStates.length > 0) {
						await BackgroundGeolocation.removeGeofences().then(async () => {
							for (const geofence of subscribedAutomaticUserStates) {
								await BackgroundGeolocation.addGeofence({
									extras: {
										apiKey: 'fcab2a0c-54b2-460d-9f6d-ee5ed64b585a',
										locationId: String(geofence.id),
										locationName: String(geofence.name),
										userId: authenticatedUserId,
									},
									identifier: String(geofence.id),
									latitude: geofence.latitude as number,
									longitude: geofence.longitude as number,
									notifyOnDwell: false,
									notifyOnEntry: true,
									notifyOnExit: true,
									radius: geofence.radius as number,
								});
							}
						});
					} else {
						await BackgroundGeolocation.removeGeofences().then(async () => {
							await BackgroundGeolocation.stop();
						});
						void userStateStore.deleteActiveAutomaticUserState({
							activeAutomaticUserState: undefined,
							commandBus: this.commandBus,
						});
					}
				}
			},
			{ immediate: true }
		);
	}

	watchSubscribedAutomaticUserStates() {
		if (!isRunningInNativeCapacitorApp()) {
			return;
		}

		const userStateStore = useUserStateStore();
		watch(
			() => userStateStore.subscribedAutomaticUserStates,

			async (subscribedAutomaticUserStates: Array<AutomaticUserState>) => {
				if (subscribedAutomaticUserStates.length > 0) {
					await this.initializeGeofencing();
					const authenticatedUserId = useUserStore().authenticatedUserInformation?.id as number;

					await BackgroundGeolocation.removeGeofences().then(async () => {
						for (const geofence of subscribedAutomaticUserStates) {
							await BackgroundGeolocation.addGeofence({
								extras: {
									apiKey: 'fcab2a0c-54b2-460d-9f6d-ee5ed64b585a',
									locationId: String(geofence.id),
									locationName: String(geofence.name),
									userId: authenticatedUserId,
								},
								identifier: String(geofence.id),
								latitude: geofence.latitude as number,
								longitude: geofence.longitude as number,
								notifyOnDwell: false,
								notifyOnEntry: true,
								notifyOnExit: true,
								radius: geofence.radius as number,
							});
						}
					});
				} else {
					await BackgroundGeolocation.removeGeofences().then(async () => {
						await BackgroundGeolocation.stop();
					});
					void userStateStore.deleteActiveAutomaticUserState({
						activeAutomaticUserState: undefined,
						commandBus: this.commandBus,
					});
				}
			},
			{ immediate: true }
		);
	}
}
